/**
 * Round and match flow (DECISIONS.md #12, #13): countdown → live 3:00 → death or time-up →
 * higher HP% wins, exact tie → sudden death (shields off, HP drains) → round-end banner → next
 * round with swapped spawns, or match end after two wins. Simultaneous deaths go to the higher
 * HP% at the start of the tick; an exact tie is settled by the match's own random number.
 */
import { SIM_DT } from '../constants.js';
import { rngFloat } from '../sim/rng.js';
import { applyDamage, kill } from './damage.js';
import { computeResult, computeScore } from './scoring.js';
import { cloneMatchState, resetRoundState, secondsToTicks } from './state.js';
import { defOf, emit, mech } from './tick.js';
function hpFraction(t, slot) {
    return mech(t, slot).hp / defOf(t, slot).hp;
}
function endRound(t, winner, reason, liveTicksLeft) {
    const s = t.s;
    const f = s.flow;
    f.wins[winner]++;
    s.stats[winner].roundsWon++;
    s.stats[winner].timeBonusTicks += reason === 'suddenDeath' ? 0 : liveTicksLeft;
    s.rounds.push({
        round: f.round,
        winner,
        reason,
        ticks: s.tick - f.liveTick,
        hpPct: [hpFraction(t, 0), hpFraction(t, 1)],
    });
    f.roundWinner = winner;
    emit(t, { t: 'roundEnd', round: f.round, winner, reason });
    if (f.wins[winner] >= s.config.roundsToWin) {
        f.phase = 'matchEnd';
        f.ticksLeft = 0;
        s.result = computeResult(t);
        emit(t, { t: 'matchEnd', winner });
        return;
    }
    f.phase = 'roundEnd';
    f.ticksLeft = secondsToTicks(s.config.roundEndSeconds);
}
/** Resolves deaths that happened this tick; returns true when the round ended. */
function resolveDeaths(t, prev, liveTicksLeft) {
    const dead0 = !t.s.mechs[0].alive;
    const dead1 = !t.s.mechs[1].alive;
    if (!dead0 && !dead1)
        return false;
    if (dead0 && dead1) {
        const a = prev.mechs[0].hp / t.ctx.defs[0].hp;
        const b = prev.mechs[1].hp / t.ctx.defs[1].hp;
        const winner = a > b ? 0 : b > a ? 1 : rngFloat(t.s.rng) < 0.5 ? 0 : 1;
        endRound(t, winner, 'mutual', liveTicksLeft);
        return true;
    }
    endRound(t, dead0 ? 1 : 0, 'kill', liveTicksLeft);
    return true;
}
function startNextRound(t, magazines) {
    const s = t.s;
    const f = s.flow;
    f.round++;
    f.swapped = !f.swapped;
    f.roundWinner = null;
    resetRoundState(s, t.ctx, magazines);
    if (s.config.countdownSeconds > 0) {
        f.phase = 'countdown';
        f.ticksLeft = secondsToTicks(s.config.countdownSeconds);
    }
    else {
        f.phase = 'live';
        f.ticksLeft = secondsToTicks(s.config.roundSeconds);
        f.liveTick = s.tick;
    }
    emit(t, { t: 'roundStart', round: f.round });
    if (f.phase === 'live')
        emit(t, { t: 'live', round: f.round });
}
/** Kill-volume deaths (fell out of the arena) count as deaths with no killer. */
export function applyKillVolume(t) {
    for (const slot of [0, 1]) {
        const c = mech(t, slot);
        if (c.alive && !c.body.alive)
            kill(t, slot, null);
    }
}
export function stepFlow(t, prev, magazines) {
    const s = t.s;
    const f = s.flow;
    switch (f.phase) {
        case 'countdown':
            f.ticksLeft--;
            if (f.ticksLeft <= 0) {
                f.phase = 'live';
                f.ticksLeft = secondsToTicks(s.config.roundSeconds);
                f.liveTick = s.tick;
                emit(t, { t: 'live', round: f.round });
            }
            return;
        case 'live': {
            f.ticksLeft = Math.max(0, f.ticksLeft - 1);
            if (resolveDeaths(t, prev, f.ticksLeft))
                return;
            if (f.ticksLeft > 0)
                return;
            const a = hpFraction(t, 0);
            const b = hpFraction(t, 1);
            if (a !== b) {
                endRound(t, a > b ? 0 : 1, 'time', 0);
                return;
            }
            f.phase = 'suddenDeath';
            s.mechs[0].shield = 0;
            s.mechs[1].shield = 0;
            emit(t, { t: 'suddenDeath', round: f.round });
            return;
        }
        case 'suddenDeath': {
            for (const slot of [0, 1]) {
                const c = mech(t, slot);
                if (!c.alive)
                    continue;
                const def = defOf(t, slot);
                applyDamage(t, {
                    target: slot,
                    source: slot,
                    amount: def.hp * t.ctx.balance.suddenDeath.drainPerSecond * SIM_DT,
                    kind: 'suddenDeath',
                    location: 'torso',
                    rear: false,
                    position: { ...c.body.position },
                    direct: true,
                });
            }
            if (!t.s.mechs[0].alive || !t.s.mechs[1].alive) {
                const dead0 = !t.s.mechs[0].alive;
                const dead1 = !t.s.mechs[1].alive;
                if (dead0 && dead1) {
                    const a = prev.mechs[0].hp / t.ctx.defs[0].hp;
                    const b = prev.mechs[1].hp / t.ctx.defs[1].hp;
                    endRound(t, a > b ? 0 : b > a ? 1 : rngFloat(t.s.rng) < 0.5 ? 0 : 1, 'suddenDeath', 0);
                }
                else {
                    endRound(t, dead0 ? 1 : 0, 'suddenDeath', 0);
                }
            }
            return;
        }
        case 'roundEnd':
            f.ticksLeft--;
            if (f.ticksLeft <= 0)
                startNextRound(t, magazines);
            return;
        case 'matchEnd':
            return;
    }
}
/**
 * Ends the match by forfeit (DECISIONS.md #23): the leaver takes the loss with no credits, the
 * winner is paid in full; a double forfeit (`winner` null) is a draw that pays nobody. Pure: the
 * result is a new state whose events carry the round-end and match-end banners. A match that
 * already ended is returned unchanged.
 */
export function forfeitMatch(prev, ctx, winner) {
    const s = cloneMatchState(prev);
    if (s.flow.phase === 'matchEnd')
        return s;
    const f = s.flow;
    const roundTicks = secondsToTicks(s.config.roundSeconds);
    if (winner !== null) {
        f.wins[winner]++;
        s.stats[winner].roundsWon++;
    }
    s.rounds.push({
        round: f.round,
        winner,
        reason: 'forfeit',
        ticks: Math.max(0, s.tick - f.liveTick),
        hpPct: [s.mechs[0].hp / ctx.defs[0].hp, s.mechs[1].hp / ctx.defs[1].hp],
    });
    f.roundWinner = winner;
    f.phase = 'matchEnd';
    f.ticksLeft = 0;
    const scores = [0, 1].map((slot) => {
        const won = winner === slot;
        const score = computeScore(s.stats[slot], won, s.config.mode, ctx.balance, roundTicks);
        return won ? score : { ...score, credits: 0 };
    });
    s.result = { winner, reason: 'forfeit', scores };
    s.events.push({ t: 'roundEnd', round: f.round, winner, reason: 'forfeit' });
    s.events.push({ t: 'matchEnd', winner });
    return s;
}
//# sourceMappingURL=flow.js.map